import type { MessageWithBody, SingleConversation } from "@grape-law-firm/grape-common-missive-client";
import type { MissiveGetConversationDetailsReqBody } from "~/server/api/missive/getConversationDetails.post";
import type { MissiveContact, MissiveConversationMessage } from "~/types/missive";
import { PhoneNumberAnalyzer } from "@grape-law-firm/grape-common-utils";
import { AnalyticsEvent } from "~/types/analytics";
import { isDevOrStaging } from "~/utils/dev";

export const useMissiveStore = defineStore("missive", () => {
  const { $posthog } = useNuxtApp();
  const toast = useToast();

  const client = computed(() => window.Missive);
  const active = computed(() => !!client.value?.id);
  const user = computed(() => {
    if (!client.value || !client.value.user) {
      if (isDevOrStaging()) {
        const { missiveDevEmail } = useRuntimeConfig().public;
        if (!missiveDevEmail) {
          throw createError({
            message: "You need to configure the environment variables correctly to access the app in development mode.",
            fatal: true,
          });
        }

        return {
          id: "dev",
          avatar: "",
          email: missiveDevEmail,
        };
      }
      return null;
    }

    return client.value.user;
  });

  // We fetch the conversations from Missive when the user clicks on a conversation.
  const selectedConversationIds = ref<string[]>([]);

  const selectedConversations = ref<Array<SingleConversation & {
    email_addresses: Array<{ address: string; name: string; }>;
    phone_numbers: Array<{ value: string; name: string; }>;
    messages: MessageWithBody[];
    link: string;
  }>>([]);

  const selectedConversationMessages = ref<MissiveConversationMessage[]>([]);

  const contactsInConversation = ref<MissiveContact[]>(import.meta.dev ? [{ email: "mnkasikci@gmail.com" }] : []);

  watch(active, (active) => {
    if (!active)
      return;

    client.value.on("change:conversations", (ids: string[]) => {
      selectedConversationIds.value = ids;
      contactsInConversation.value = [];

      client.value.fetchConversations(ids).then((res) => {
        selectedConversations.value = res as any;
        for (const c of selectedConversations.value) {
          contactsInConversation.value.push(...c.email_addresses.map(e => ({ name: e.name ?? "", email: e.address })));
          contactsInConversation.value.push(...c.phone_numbers.map(e => ({ name: e.name ?? "", phoneNumber: e.value })));
        }
      });
    });
  }, { immediate: true });

  // Get more contacts from the conversation messages meta data and content
  const getFullConversationDetailsPending = ref(false);

  /**
   * This uses the REST API to fetch all the contacts from the meta fields of the full conversation and also the bodies of the messages.
   */
  async function getFullConversationDetails() {
    if (!selectedConversationIds.value.length)
      return;

    // We don't clear the contactsInConversation array because we want to keep the contacts that we've already got from Missive iFrame API
    // Clear contacts before fetching new details
    getFullConversationDetailsPending.value = true;

    const getLastThreeDaysTimestamp = (): number => {
      const now = Math.floor(Date.now() / 1000);
      const threeDaysInSeconds = 3 * 24 * 60 * 60;
      return now - threeDaysInSeconds;
    };

    try {
      const start = new Date();
      const res = await $fetch("/api/missive/getConversationDetails", {
        method: "POST",
        body: {
          conversationIds: selectedConversationIds.value,
          sinceUnixTimeStampSeconds: getLastThreeDaysTimestamp(),
        } satisfies MissiveGetConversationDetailsReqBody,
        retry: 3,
      });
      const end = new Date();
      $posthog.capture(AnalyticsEvent.MissiveContactsFetched, {
        duration: end.getTime() - start.getTime(),
      });

      const uniqueContacts = new Set(
        contactsInConversation.value.map(c =>
          c.integrations?.hubspotData?.id || `${c.email || ""}-${c.phoneNumber || ""}`.toLowerCase().trim(),
        ),
      );

      // We only add new contacts
      for (const c of res.contacts) {
        const contactKey = c.integrations?.hubspotData?.id || `${c.email || ""}-${c.phoneNumber || ""}`.toLowerCase().trim();

        if (!uniqueContacts.has(contactKey)) {
          uniqueContacts.add(contactKey);
          contactsInConversation.value.push(c);
        }
      }

      // We set the messages for future use
      selectedConversationMessages.value = res.bodies;
    }
    catch (error: any) {
      console.error(error);
      toast.add({
        icon: "i-heroicons-x-circle-solid",
        title: "Failed to fetch Missive contacts",
        description: error.message || "Please re-select the conversation and try again.",
        color: "red",
      });
    }

    getFullConversationDetailsPending.value = false;
  }

  // Filter contacts
  const externalContactsInConversation = computed(() => {
    const filtered: MissiveContact[] = [];
    const uniqueContacts = new Set<string>();

    if (!contactsInConversation.value?.length)
      return filtered;

    // We exclude internal contacts
    for (const c of contactsInConversation.value) {
      const excludedDomains = ["grapelaw.com", "hubspot.com", "manaycpa.com", "visapeer.com", "calendly.com"];
      const excludedNumbers = ["12124338383", "12124338484", "14403284848", "16466933993", "18444231444", "15513054838", "16468256556"];

      if (c.email && excludedDomains.some(d => c.email?.includes(d)))
        continue;

      // Exclude our phone numbers
      const cleanedPhoneNumber = c.phoneNumber ? PhoneNumberAnalyzer.cleanNumber(c.phoneNumber!) : null;
      if (cleanedPhoneNumber && excludedNumbers.some(n => n.includes(cleanedPhoneNumber)))
        continue;

      // Sometimes our numbers appear in the name field
      const cleanedName = c.name ? PhoneNumberAnalyzer.cleanNumber(c.name) : null;
      if (cleanedName && excludedNumbers.some(n => n.includes(cleanedName)))
        continue;

      // Exclude our Instagram account
      const normalizedUsername = c.username?.normalize("NFD").replace(/[\u0300-\u036F]/g, "").toLowerCase().trim();
      const normalizedName = c.name?.normalize("NFD").replace(/[\u0300-\u036F]/g, "").toLowerCase().trim();
      if ([normalizedName, normalizedUsername].includes("avukat muhammed uzum") || normalizedUsername === "uzumesq")
        continue;

      // Generates a unique key for each contact based on email, cleaned phone number, and normalized name
      const uniqueKey = c.integrations?.hubspotData
        ? c.integrations.hubspotData.id
        : `${c.email || ""}-${cleanedPhoneNumber || ""}`;
      if (!uniqueContacts.has(uniqueKey)) {
        uniqueContacts.add(uniqueKey);
        filtered.push(c);
      }
    }

    return filtered;
  });

  return {
    active,
    client,
    user,
    selectedConversationIds,
    selectedConversations,
    selectedConversationMessages,
    contactsInConversation,
    externalContactsInConversation,
    getFullConversationDetails,
    getFullConversationDetailsPending,
  };
});
